import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { PrivateRoute } from "@/auth/PrivateRoute";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

// Public
import HomePage from "@/pages/public/HomePage";
import DoctorDirectoryPage from "@/pages/public/DoctorDirectoryPage";
import DoctorDetailPage from "@/pages/public/DoctorDetailPage";
import HowItWorksPage from "@/pages/public/HowItWorksPage";
import PrivacyPage from "@/pages/public/PrivacyPage";
import DeleteAccountPage from "@/pages/public/DeleteAccountPage";
import ForDoctorsPage from "@/pages/public/ForDoctorsPage";
import UnauthorizedPage from "@/pages/public/UnauthorizedPage";
import PrescriptionVerifyPage from "@/pages/public/PrescriptionVerifyPage";
import NotFoundPage from "@/pages/public/NotFoundPage";

// Patient
import PatientDashboard from "@/pages/patient/PatientDashboard";
import PatientAppointmentDetailPage from "@/pages/patient/PatientAppointmentDetailPage";
import MyAppointmentsPage from "@/pages/patient/MyAppointmentsPage";
import BookAppointmentPage from "@/pages/patient/BookAppointmentPage";
import PatientProfilePage from "@/pages/patient/PatientProfilePage";
import PaymentsPage from "@/pages/patient/PaymentsPage";
import PaymentPage from "@/pages/patient/PaymentPage";
import ConsultationRoomPage from "@/pages/consult/ConsultationRoomPage";

// Doctor
import DoctorDashboard from "@/pages/doctor/DoctorDashboard";
import DoctorAppointmentsPage from "@/pages/doctor/DoctorAppointmentsPage";
import DoctorSchedulePage from "@/pages/doctor/DoctorSchedulePage";
import DoctorProfilePage from "@/pages/doctor/DoctorProfilePage";
import DoctorVerificationPage from "@/pages/doctor/DoctorVerificationPage";
import DoctorOnboardingPage from "@/pages/doctor/DoctorOnboardingPage";

// Admin
import AdminDoctorsPage from "@/pages/admin/AdminDoctorsPage";
import AdminRefundsPage from "@/pages/admin/AdminRefundsPage";
import AdminAppointmentsPage from "@/pages/admin/AdminAppointmentsPage";

/**
 * Root route behaviour: we want the "/" URL to mean different things
 * depending on who's looking at it.
 *
 * - Guests: the marketing-style HomePage (lets them browse doctors,
 *   "How it works", etc.).
 * - Signed-in patients, doctors, and admins: their role dashboard. A
 *   logged-in user seeing the marketing homepage was confusing — the
 *   dashboard is their real landing page.
 *
 * While a freshly-registered account is being promoted to doctor we stay
 * on HomePage so the top-level spinner/promotion effect in AuthProvider
 * can finish without us bouncing the user to /patient/dashboard first.
 */
function RoleAwareHome() {
  const { isAuthenticated, isPatient, isDoctor, isAdmin, promotingToDoctor } =
    useAuth();
  if (isAuthenticated && isDoctor) {
    return <Navigate to="/doctor/dashboard" replace />;
  }
  if (isAuthenticated && isAdmin) {
    return <Navigate to="/admin/doctors" replace />;
  }
  if (isAuthenticated && isPatient && !promotingToDoctor) {
    return <Navigate to="/patient/dashboard" replace />;
  }
  return <HomePage />;
}

export default function App() {
  const { initialized } = useAuth();
  const [initTimedOut, setInitTimedOut] = useState(false);

  // Safety net: if auth init never resolves (identity service unreachable,
  // silent-check-sso iframe silently failing, etc.) the
  // top-level spinner would otherwise hang forever and look like a
  // frozen "gray overlay" with no way to interact with the page.
  useEffect(() => {
    if (initialized) {
      setInitTimedOut(false);
      return;
    }
    const t = window.setTimeout(() => setInitTimedOut(true), 15_000);
    return () => window.clearTimeout(t);
  }, [initialized]);

  if (!initialized) {
    return (
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          minHeight: "100vh",
          p: 3,
        }}
      >
        {initTimedOut ? (
          <Stack spacing={2} sx={{ maxWidth: 480, textAlign: "center" }}>
            <Alert severity="warning">
              We could not reach the sign-in service. Please check that it is
              running and reachable, then reload.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              If this keeps happening, open the browser DevTools console and
              check for network or script errors.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </Stack>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* ─── Public (shell: Navbar + Footer) ─── */}
        <Route element={<PublicLayout />}>
          <Route index element={<RoleAwareHome />} />
          <Route path="/doctors" element={<DoctorDirectoryPage />} />
          <Route path="/doctors/:id" element={<DoctorDetailPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/for-doctors" element={<ForDoctorsPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route
            path="/prescription/verify/:code"
            element={<PrescriptionVerifyPage />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* ─── Patient (dashboard shell, ROLE_PATIENT) ─── */}
        <Route
          element={
            <PrivateRoute requiredRole="ROLE_PATIENT">
              <DashboardLayout role="patient" />
            </PrivateRoute>
          }
        >
          <Route path="/patient" element={<Navigate to="/patient/dashboard" replace />} />
          <Route path="/patient/dashboard" element={<PatientDashboard />} />
          <Route path="/patient/appointments" element={<MyAppointmentsPage />} />
          <Route
            path="/patient/appointments/:id"
            element={<PatientAppointmentDetailPage />}
          />
          <Route
            path="/patient/appointments/:id/pay"
            element={<PaymentPage />}
          />
          <Route path="/patient/book/:doctorKeycloakId" element={<BookAppointmentPage />} />
          <Route path="/patient/payments" element={<PaymentsPage />} />
          <Route path="/patient/profile" element={<PatientProfilePage />} />
          <Route
            path="/patient/consult/:appointmentId"
            element={<ConsultationRoomPage />}
          />
        </Route>

        {/* ─── Doctor (dashboard shell, ROLE_DOCTOR) ─── */}
        <Route
          element={
            <PrivateRoute requiredRole="ROLE_DOCTOR">
              <DashboardLayout role="doctor" />
            </PrivateRoute>
          }
        >
          <Route path="/doctor" element={<Navigate to="/doctor/dashboard" replace />} />
          <Route path="/doctor/dashboard" element={<DoctorDashboard />} />
          <Route path="/doctor/onboarding" element={<DoctorOnboardingPage />} />
          <Route path="/doctor/appointments" element={<DoctorAppointmentsPage />} />
          <Route path="/doctor/schedule" element={<DoctorSchedulePage />} />
          <Route path="/doctor/verification" element={<DoctorVerificationPage />} />
          <Route path="/doctor/profile" element={<DoctorProfilePage />} />
          <Route
            path="/doctor/consult/:appointmentId"
            element={<ConsultationRoomPage />}
          />
        </Route>

        {/* ─── Admin (dashboard shell, ROLE_ADMIN) ─── */}
        <Route
          element={
            <PrivateRoute requiredRole="ROLE_ADMIN">
              <DashboardLayout role="admin" />
            </PrivateRoute>
          }
        >
          <Route path="/admin" element={<Navigate to="/admin/doctors" replace />} />
          <Route path="/admin/doctors" element={<AdminDoctorsPage />} />
          <Route path="/admin/appointments" element={<AdminAppointmentsPage />} />
          <Route path="/admin/refunds" element={<AdminRefundsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
